"use client";

import clsx from "clsx";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import type {
   CreateBasicAssistantRequest,
   CreateBasicAssistantResponse,
} from "@/app/api/assistants/create-basic/route";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import {Industry} from "@/enums/onboarding";
import {useOrg} from "@/hooks/useOrg";
import {useUserProfile} from "@/hooks/useUserProfile";
import { apiFetch } from "@/lib/api";
import {TRIAL_SEEDED_ASSISTANT_MODEL} from "@/types/ai";
import {createMinDurationEnforcer} from "@/utils/onboarding/common";
import {OnboardingStepHeader} from "../OnboardingStepHeader";
import {ScriptBuildingLoader} from "../ScriptBuildingLoader";

const GAILGPT_WORKSTREAM = [
   "indexBusinessProfile(config.org)",
   "loadIndustryKnowledge(org.industries)",
   "buildFaqCorpus(website, services)",
   "trainResponsePatterns(corpus)",
   "configureConversationStyle('professional')",
   "setContextWindow({ depth: 'deep' })",
   "mapTopicRouting(knowledgeBase)",
   "initChatEngine({ model: 'gailgpt-v2' })",
   "runAccuracyValidation(testSuite)",
   "calibrateConfidenceThresholds()",
   "deployAssistant({ status: 'active' })",
   "registerWebhooks(assistant.id)",
   "finalizeAssistantConfig()",
   "exportAssistantBundle({ format: 'v1' })",
];

// GailGPT focus options
const GAILGPT_FOCUS_OPTIONS = {
   ANSWER_QUESTIONS: "answerQuestions",
   DRAFT_MESSAGES: "draftMessages",
} as const;

interface ConfigureGailGptStepProps {
   readonly selectedFocus: string[];
   readonly onBack?: () => void;
   readonly onNext: () => void;
   readonly isLastStep?: boolean;
}

type GailGptSetupStatus =
   | {state: "idle"}
   | {state: "generating"}
   | {state: "alreadyCompleted"; assistantId: string}
   | {state: "success"; assistantId: string}
   | {state: "error"; message?: string};

export function ConfigureGailGptStep({
   selectedFocus,
   onBack,
   onNext,
   isLastStep = false,
}: ConfigureGailGptStepProps) {
   const {t} = useTranslation("onboarding");
   const {org, saveOrg} = useOrg();
   const {userProfile} = useUserProfile();

   const setupRef = useRef(false);

   // Wait for org to load before making decisions
   const isOrgLoaded = org !== null && org !== undefined;
   const existingAssistantId = org?.details?.onboarding?.gailGptAssistantId;

   // Determine which GailGPT options are selected
   const hasAnswerQuestions = selectedFocus.includes(GAILGPT_FOCUS_OPTIONS.ANSWER_QUESTIONS);
   const hasDraftMessages = selectedFocus.includes(GAILGPT_FOCUS_OPTIONS.DRAFT_MESSAGES);

   // State - start in idle, will be updated once org loads
   const [status, setStatus] = useState<GailGptSetupStatus>({state: "idle"});

   

   // Build the description for the assistant based on org data and selected focuses
   const buildAssistantDescription = useCallback((): string => {
      const orgName = org?.name || "your organization";
      const industries = (org?.details?.industries as Industry[]) || [];
      const services = (org?.details?.services as string[]) || [];

      // Build industry string
      const industryStr =
         industries.length > 0
            ? industries.map((ind) => t(`industryServices.industries.${ind}`)).join(", ")
            : "";

      // Build services string
      const servicesStr =
         services.length > 0
            ? services
                 .slice(0, 5)
                 .map((service) => {
                    const translated = t(`industryServices.services.${service}`, service);
                    // If translation key doesn't exist, use the raw service name
                    return translated === `industryServices.services.${service}`
                       ? service
                       : translated;
                 })
                 .join(", ")
            : "";

      // Build capabilities based on selected focuses using the same translations the user saw
      const capabilities: string[] = [];
      if (hasAnswerQuestions) {
         capabilities.push(t(`chooseGailFocus.options.${GAILGPT_FOCUS_OPTIONS.ANSWER_QUESTIONS}`));
      }
      if (hasDraftMessages) {
         capabilities.push(t(`chooseGailFocus.options.${GAILGPT_FOCUS_OPTIONS.DRAFT_MESSAGES}`));
      }

      // Construct the description
      let description = `An AI assistant for ${orgName}`;

      if (industryStr) {
         description += ` in the ${industryStr} industry`;
      }

      if (servicesStr) {
         description += `, offering services such as ${servicesStr}`;
      }

      description += ".";

      if (capabilities.length > 0) {
         description += ` This assistant can: ${capabilities.join(". ")}.`;
      }

      return description;
   }, [org, t, hasAnswerQuestions, hasDraftMessages]);

   // Create the GailGPT assistant
   const createGailGptAssistant = useCallback(async () => {
      if (setupRef.current || status.state === "generating") {
         return;
      }

      setupRef.current = true;
      setStatus({state: "generating"});

      const enforceMinDuration = createMinDurationEnforcer(20_000);

      try {
         const description = buildAssistantDescription();

         // Determine specialty based on selected focuses
         let specialty = "AI Assistant";
         if (hasAnswerQuestions && hasDraftMessages) {
            specialty = "Customer Support & Communications Assistant";
         } else if (hasAnswerQuestions) {
            specialty = "Customer Support Assistant";
         } else if (hasDraftMessages) {
            specialty = "Communications Assistant";
         }

         const requestData: CreateBasicAssistantRequest = {
            gailName: "Gail",
            specialty,
            description,
            language: userProfile.language,
            gptModel: TRIAL_SEEDED_ASSISTANT_MODEL,
            webSearchEnabled: true,
         };

         const response = await apiFetch<CreateBasicAssistantResponse>(
           "/api/assistants/create-basic",
           "POST",
           {
           body: requestData,
         },
         );

         if (!response?.success || !response.assistant?.id) {
            throw new Error(response?.error ?? "Failed to create assistant");
         }

         const assistantId = response.assistant.id;

         // Save the assistant ID to org details
         saveOrg(
            {
               details: {
                  ...org?.details,
                  onboarding: {
                     ...org?.details?.onboarding,
                     status: org?.details?.onboarding?.status ?? "in_progress",
                     maxCompletedStep: org?.details?.onboarding?.maxCompletedStep ?? 1,
                     gailGptAssistantId: assistantId,
                  },
               },
            },
            {silent: true}
         );

         await enforceMinDuration();
         setStatus({state: "success", assistantId});
      } catch (error) {
         console.error("ONBOARDING::GAILGPT::ASSISTANT_CREATION_FAILED", error);
         await enforceMinDuration();
         setupRef.current = false;
         setStatus({
            state: "error",
            message: error instanceof Error ? error.message : "Failed to create assistant",
         });
      }
   }, [
      status.state,
      buildAssistantDescription,
      hasAnswerQuestions,
      hasDraftMessages,
      userProfile.language,
      org?.details,
      saveOrg,
   ]);

   // Handle org loading and existing assistant check
   useEffect(() => {
      // Wait for org to load before making any decisions
      if (!isOrgLoaded) return;

      // If we already have an assistant, show already completed state
      if (existingAssistantId) {
         setStatus({state: "alreadyCompleted", assistantId: existingAssistantId});
         return;
      }

      // Org loaded and no existing assistant, safe to create
      if (status.state === "idle") {
         createGailGptAssistant();
      }
   }, [isOrgLoaded, existingAssistantId, status.state, createGailGptAssistant]);

   // Render loading states (idle = waiting for org, generating = creating assistant)
   if (status.state === "idle" || status.state === "generating") {
      return (
         <ScriptBuildingLoader
            title={t("configureGail.gailgpt.generatingTitle")}
            subtitle={t("configureGail.gailgpt.generatingDescription")}
            messages={t("configureGail.gailgpt.buildingLoader.messages", {returnObjects: true}) as string[]}
            codeLines={GAILGPT_WORKSTREAM}
            metricLabels={t("configureGail.gailgpt.buildingLoader.metrics", {returnObjects: true}) as [string, string, string]}
         />
      );
   }

   // Render already completed state - assistant was created in a previous session
   if (status.state === "alreadyCompleted") {
      return (
         <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-8">
               <div className="w-full max-w-2xl mx-auto space-y-6">
                  <OnboardingStepHeader
                     title={t("configureGail.gailgpt.alreadyCompleted.title")}
                     description={t("configureGail.gailgpt.alreadyCompleted.description")}
                  />

                  <div
                     className={clsx(
                        "rounded-2xl border border-emerald-500/50 bg-emerald-500/5 p-8",
                        "text-center space-y-4"
                     )}
                  >
                     <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                        <Icon icon="circle-check" className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                     </div>
                     <div className="space-y-2">
                        <h3 className="text-xl font-semibold text-emerald-700 dark:text-emerald-300">
                           {t("configureGail.gailgpt.success.created")}
                        </h3>
                        <p className="text-muted-foreground">
                           {t("configureGail.gailgpt.alreadyCompleted.manageInfo")}
                        </p>
                     </div>
                  </div>

                  {/* Link to GailGPT */}
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                     <div className="flex items-center justify-between gap-4">
                        <div className="space-y-1">
                           <h4 className="font-medium text-foreground">
                              {t("configureGail.gailgpt.success.tryItOut")}
                           </h4>
                           <p className="text-sm text-muted-foreground">
                              {t("configureGail.gailgpt.success.tryItOutDescription")}
                           </p>
                        </div>
                        <Button
                           type="secondary"
                           compact
                           href="/gailgpt"
                           target="_blank"
                           iconAfter={<Icon icon="arrow-up-right-from-square" className="w-4 h-4" />}
                        >
                           {t("configureGail.gailgpt.alreadyCompleted.viewAssistant")}
                        </Button>
                     </div>
                  </div>
               </div>
            </div>

            {/* Footer */}
            <div
               className={clsx(
                  "sticky bottom-0 flex items-center gap-4 px-8 py-4",
                  "bg-panel_secondary/50 border-t border-200 backdrop-blur-md"
               )}
            >
               {onBack && (
                  <Button type="tertiary" className="min-w-[120px]" onClick={onBack}>
                     {t("wizard.back")}
                  </Button>
               )}
               <Button type="primary" className="min-w-[120px] ml-auto" onClick={onNext}>
                  {isLastStep ? t("wizard.done") : t("wizard.next")}
               </Button>
            </div>
         </div>
      );
   }

   // Render error state
   if (status.state === "error") {
      return (
         <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-8">
               <div className="w-full max-w-2xl mx-auto space-y-6">
                  <OnboardingStepHeader
                     title={t("configureGail.gailgpt.title")}
                     description={t("configureGail.gailgpt.description")}
                  />

                  <div
                     className={clsx(
                        "rounded-2xl border border-destructive/50 bg-destructive/5 p-6",
                        "text-center space-y-4"
                     )}
                  >
                     <Icon icon="circle-xmark" className="w-8 h-8 text-destructive mx-auto" />
                     <div className="space-y-2">
                        <h3 className="font-semibold text-destructive">
                           {t("configureGail.gailgpt.error.title")}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                           {status.message ?? t("configureGail.gailgpt.error.generic")}
                        </p>
                     </div>
                     <Button
                        type="primary"
                        onClick={() => {
                           setupRef.current = false;
                           setStatus({state: "idle"});
                        }}
                     >
                        {t("configureGail.gailgpt.error.retry")}
                     </Button>
                  </div>
               </div>
            </div>

            {/* Footer */}
            <div
               className={clsx(
                  "sticky bottom-0 flex items-center gap-4 px-8 py-4",
                  "bg-panel_secondary/50 border-t border-200 backdrop-blur-md"
               )}
            >
               {onBack && (
                  <Button type="tertiary" className="min-w-[120px]" onClick={onBack}>
                     {t("wizard.back")}
                  </Button>
               )}
               <Button type="primary" className="min-w-[120px] ml-auto" onClick={onNext} disabled>
                  {isLastStep ? t("wizard.done") : t("wizard.next")}
               </Button>
            </div>
         </div>
      );
   }

   // Render success state
   return (
      <div className="flex flex-col h-full">
         <div className="flex-1 overflow-y-auto p-8">
            <div className="w-full max-w-2xl mx-auto space-y-6">
               <OnboardingStepHeader
                  title={t("configureGail.gailgpt.success.title")}
                  description={t("configureGail.gailgpt.success.description")}
               />

               <div
                  className={clsx(
                     "rounded-2xl border border-emerald-500/50 bg-emerald-500/5 p-8",
                     "text-center space-y-4"
                  )}
               >
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                     <Icon icon="circle-check" className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="space-y-2">
                     <h3 className="text-xl font-semibold text-emerald-700 dark:text-emerald-300">
                        {t("configureGail.gailgpt.success.created")}
                     </h3>
                     <p className="text-muted-foreground">
                        {t("configureGail.gailgpt.success.readyToUse")}
                     </p>
                  </div>
               </div>

               {/* What Gail can do */}
               <div className="rounded-xl border border-200 bg-panel p-6 space-y-4">
                  <h4 className="font-medium text-foreground flex items-center gap-2">
                     <Icon icon="message" className="w-5 h-5 text-primary" />
                     {t("configureGail.gailgpt.success.capabilities")}
                  </h4>
                  <ul className="space-y-3">
                     {hasAnswerQuestions && (
                        <li className="flex items-start gap-3">
                           <Icon icon="circle-check" className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                           <span className="text-sm text-muted-foreground">
                              {t(
                                 `chooseGailFocus.options.${GAILGPT_FOCUS_OPTIONS.ANSWER_QUESTIONS}`
                              )}
                           </span>
                        </li>
                     )}
                     {hasDraftMessages && (
                        <li className="flex items-start gap-3">
                           <Icon icon="circle-check" className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                           <span className="text-sm text-muted-foreground">
                              {t(`chooseGailFocus.options.${GAILGPT_FOCUS_OPTIONS.DRAFT_MESSAGES}`)}
                           </span>
                        </li>
                     )}
                  </ul>
               </div>

               {/* Try it out card */}
               <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                  <div className="flex items-center justify-between gap-4">
                     <div className="space-y-1">
                        <h4 className="font-medium text-foreground">
                           {t("configureGail.gailgpt.success.tryItOut")}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                           {t("configureGail.gailgpt.success.tryItOutDescription")}
                        </p>
                     </div>
                     <Button
                        type="secondary"
                        compact
                        href="/gailgpt"
                        target="_blank"
                        secondaryAction={onNext}
                        iconAfter={<Icon icon="arrow-up-right-from-square" className="w-4 h-4" />}
                     >
                        {t("configureGail.gailgpt.success.openGailGpt")}
                     </Button>
                  </div>
               </div>
            </div>
         </div>

         {/* Footer */}
         <div
            className={clsx(
               "sticky bottom-0 flex items-center gap-4 px-8 py-4",
               "bg-panel_secondary/50 border-t border-200 backdrop-blur-md"
            )}
         >
            {onBack && (
               <Button type="tertiary" className="min-w-[120px]" onClick={onBack}>
                  {t("wizard.back")}
               </Button>
            )}
            <Button type="primary" className="min-w-[120px] ml-auto" onClick={onNext}>
               {isLastStep ? t("wizard.done") : t("wizard.next")}
            </Button>
         </div>
      </div>
   );
}
